<template #default>
    <form
        v-if="status.detail && !loading"
        @submit.prevent="propertyTenantedUUID ? requestUpdate() : requestCreate()"
    >
        <div class="row">
            <div class="col-md-12">
                <h6 class="font-weight-bold text-info">
                    <i class="mdi mdi-home-outline mr-1"></i>
                    Basic Information
                </h6>
            </div>
            <div class="col-md-12">
                <b-form-group
                    :label="$t('label.image')"
                    label-cols="2"
                    label-align="left"
                >
                    <ImageUploader
                        :key="propertyTenantedUUID"
                        :multiple="false"
                        :showImageTop="true"
                        :buttonText="$t('label.uploadX', [$t('label.image')])"
                        @images-changed="uploadImage"
                        @images-removed="removeImage"
                        :initial-images="createForm.property.image ? [createForm.property.image] : []"
                        ref="coverUploader"
                    />
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group
                    label="Registered Owner"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-input
                        v-model.trim="createForm.property.registeredOwner"
                        autocomplete="off"
                    />
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group
                    :label="$t('module.company')"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-input
                        v-model.trim="createForm.property.company"
                        :class="{ 'is-invalid': submitted && !$v.createForm.property.company.required }"
                        autocomplete="off"
                    />
                    <b-form-invalid-feedback v-if="submitted && !$v.createForm.property.company.required" class="text-right">
                        {{ $t("message.requiredX", [$t("module.company")]) }}
                    </b-form-invalid-feedback>
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group
                    :label="$t('module.group')"
                    label-cols="4"
                    label-align="left"
                >
                    <multiselect
                        v-model="createForm.property.groupUuid"
                        track-by="value"
                        label="text"
                        :placeholder="'Select'"
                        :select-label="'Select'"
                        :selected-label="'Selected'"
                        :deselect-label="'Select'"
                        :options="groupDdl"
                        :searchable="true"
                        :allow-empty="false"
                    />
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group
                    :label="$t('label.type')"
                    label-cols="4"
                    label-align="left"
                >
                    <multiselect
                        v-model="createForm.property.propertyTypeUuid"
                        track-by="value"
                        label="text"
                        :placeholder="'Select'"
                        :select-label="'Select'"
                        :selected-label="'Selected'"
                        :deselect-label="'Select'"
                        :options="propertyTypeDdl"
                        :searchable="true"
                        :allow-empty="false"
                    />
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group
                    :label="$t('module.location')"
                    label-cols="4"
                    label-align="left"
                >
                    <multiselect
                        v-model="createForm.property.locationUuid"
                        track-by="value"
                        label="text"
                        :placeholder="'Select'"
                        :select-label="'Select'"
                        :selected-label="'Selected'"
                        :deselect-label="'Select'"
                        :options="locationDdl"
                        :searchable="true"
                        :allow-empty="false"
                        class="cursor-pointer"
                    />
                </b-form-group>
            </div>
            <div class="col-md-6"></div>
            <div class="col-md-6">
                <b-form-group
                    label="Address 1"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-input
                        v-model.trim="createForm.property.address1"
                        autocomplete="off"
                    />
                </b-form-group>
                <b-form-group
                    label="Address 2"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-input
                        v-model.trim="createForm.property.address2"
                        autocomplete="off"
                    />
                </b-form-group>
                <b-form-group
                    label="Address 3"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-input
                        v-model.trim="createForm.property.address3"
                        autocomplete="off"
                    />
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group
                    label="Postcode"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-input
                        v-model.trim="createForm.property.postcode"
                        :class="{ 'is-invalid': submitted && $v.createForm.property.postcode.$error }"
                        autocomplete="off"
                    />
                    <div v-if="submitted && $v.createForm.property.$error" class="invalid-feedback text-right">
                        <span v-if="!$v.createForm.property.postcode.decimal">{{ $t("message.invalidX", ["Posstcode"]) }}</span>
                    </div>
                </b-form-group>
                <b-form-group
                    label="State"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-input
                        v-model.trim="createForm.property.state"
                        autocomplete="off"
                    />
                </b-form-group>
                <b-form-group
                    label="City"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-input
                        v-model.trim="createForm.property.city"
                        autocomplete="off"
                    />
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group
                    label="Malaysia"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-checkbox 
                        v-model="createForm.property.isMalaysia"
                        switch
                        class="mt-2"
                    />
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group
                    label="Personal"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-checkbox
                        v-model="createForm.property.isPersonal"
                        switch
                        class="mt-2"
                    />
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group
                    :label="$t('label.description')"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-textarea
                        v-model.trim="createForm.property.description"
                    />
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group
                    :label="$t('label.remark')"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-textarea
                        v-model.trim="createForm.property.remark"
                    />
                </b-form-group>
            </div>
        </div>
        <hr>
        <div class="row">
            <div class="col-md-12">
                <h6 class="font-weight-bold text-info">
                    <i class="mdi mdi-account-key-outline mr-1"></i>
                    Tenanted Detail
                </h6>
            </div>
            <div class="col-md-6">
                <b-form-group
                    label="Landlord"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-input
                        v-model.trim="createForm.tenanted.landlord"
                        autocomplete="off"
                        :class="{ 'is-invalid': submitted && !$v.createForm.tenanted.landlord.required }"
                    />
                    <b-form-invalid-feedback v-if="submitted && !$v.createForm.tenanted.landlord.required" class="text-right">
                        {{ $t("message.requiredX", [$t("label.landlord")]) }}
                    </b-form-invalid-feedback>
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group
                    label="Year"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-input
                        v-model.trim="createForm.tenanted.tenureYear"
                        :class="{ 'is-invalid': submitted && $v.createForm.tenanted.tenureYear.$error }"
                        autocomplete="off"
                    />
                    <div v-if="submitted && $v.createForm.tenanted.$error" class="invalid-feedback text-right">
                        <span v-if="!$v.createForm.tenanted.tenureYear.decimal">{{ $t("message.invalidX", [$t("label.year")]) }}</span>
                    </div>
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group
                    label="Date Commenced"
                    label-cols="4"
                    label-align="left"
                >
                    <DatePicker
                        v-model="createForm.tenanted.dateCommenced"
                        placeholder="yyyy-mm-dd"
                        class="cursor-pointer"
                        :config="{
                            format: 'YYYY-MM-DD',
                            useCurrent: false,
                            icons: {
                                time: 'mdi mdi-clock-outline',
                                date: 'mdi mdi-calendar',
                                up: 'mdi mdi-chevron-up',
                                down: 'mdi mdi-chevron-down',
                                previous: 'mdi mdi-chevron-left',
                                next: 'mdi mdi-chevron-right',
                                today: 'mdi mdi-calendar-check',
                                clear: 'mdi mdi-delete-outline',
                                close: 'mdi mdi-close',
                            },
                        }"
                    />
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group
                    label="Date Expired"
                    label-cols="4"
                    label-align="left"
                >
                    <DatePicker
                        v-model="createForm.tenanted.dateExpired"
                        placeholder="yyyy-mm-dd"
                        class="cursor-pointer"
                        :config="{
                            format: 'YYYY-MM-DD',
                            useCurrent: false,
                            icons: {
                                time: 'mdi mdi-clock-outline',
                                date: 'mdi mdi-calendar',
                                up: 'mdi mdi-chevron-up',
                                down: 'mdi mdi-chevron-down',
                                previous: 'mdi mdi-chevron-left',
                                next: 'mdi mdi-chevron-right',
                                today: 'mdi mdi-calendar-check',
                                clear: 'mdi mdi-delete-outline',
                                close: 'mdi mdi-close',
                            },
                        }"
                    />
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group
                    label="Rental Payment"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-input
                        v-model.trim="createForm.tenanted.rentalPayment"
                        :class="{ 'is-invalid': submitted && $v.createForm.tenanted.rentalPayment.$error }"
                        autocomplete="off"
                    />
                    <div v-if="submitted && $v.createForm.tenanted.$error" class="invalid-feedback text-right">
                        <span v-if="!$v.createForm.tenanted.rentalPayment.decimal">{{ $t("message.invalidX", ["Rental Payment"]) }}</span>
                    </div>
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group
                    label="Purpose"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-textarea
                        v-model.trim="createForm.tenanted.purpose"
                    />
                </b-form-group>
            </div>
        </div>
        <hr class="my-3 mx-n4">
        <div class="d-flex justify-content-end w-100">
            <b-button
                type="button"
                variant="danger"
                class="mr-2 col-2"
                @click="$emit('done')"
            >{{ $t('label.cancel') }}</b-button>
            <b-button
                type="submit"
                variant="info"
                class="col-2"
                :disabled="!status.save"
            >
                <span v-if="status.save">
                    {{ propertyTenantedUUID ? $t('label.updateX', []) : $t('label.createX', []) }}
                </span>
                <div v-else>
                    <b-spinner small />
                </div>
            </b-button>
        </div>
    </form>
    <div v-else class="d-flex justify-content-center align-items-center m-3">
        <b-spinner class="m-5" variant="info" role="status"></b-spinner>
    </div>
</template>
<script>
import DatePicker from 'vue-bootstrap-datetimepicker';
import 'pc-bootstrap4-datetimepicker/build/css/bootstrap-datetimepicker.css';
import ImageUploader from '@/components/upload-image';
import Multiselect from "vue-multiselect";
import { required, decimal } from "vuelidate/lib/validators";
import { mapState } from "vuex";
import { propertyTenantedMethods, lookupMethods } from "@/state/api";
import { PROPERTY_TENANTED_MODULE } from "@/state/api/propertyTenanted.state";

export default {
    props: {
        propertyTenantedUUID: {
            type: String,
            required: false,
        },
        searchCriteria: {
            type: Object,
            required: false,
        },
    },
    validations() {
        return {
            createForm: {
                property: {
                    company: { required },
                    lat: { decimal },
                    lng: { decimal},
                    postcode: { decimal },
                },
                tenanted: {
                    landlord: { required },
                    tenureYear: { decimal },
                    rentalPayment: { decimal },
                },
            },
        };
    },
    components: { ImageUploader, Multiselect, DatePicker },
    async created() {
        await this.lookupGroup();
        await this.lookupPropertyType();
        await this.lookupLocation();
        await this.getPropertyTenantedData();

        this.loading = false;
    },
    data() {
        return {
            submitted: false,
            createForm: this.defaultForm(),
            loading: !!this.propertyTenantedUUID,
        };
    },
    methods: {
        ...propertyTenantedMethods,
        ...lookupMethods,
        defaultForm() {
            return {
                uuid: null,
                property: {
                    company: null,
                    registeredOwner: null,
                    groupUuid: null,
                    propertyTypeUuid: null,
                    locationUuid: null,
                    description: null,
                    remark: null,
                    address1: null,
                    address2: null,
                    address3: null,
                    city: null,
                    state: null,
                    postcode: null,
                    lat: null,
                    lng: null,
                    isAsset: 0,
                    isTenanted: 1,
                    isMalaysia: false,
                    isPersonal: false,
                    image: null,
                },
                tenanted: {
                    landlord: null,
                    purpose: null,
                    dateCommenced: null,
                    dateExpired: null,
                    tenureYear: null,
                    rentalPayment: null,
                },
            };
        },
        async getPropertyTenantedData() {
            if (this.propertyTenantedUUID) {
                await this.getPropertyTenanted({ uuid: this.propertyTenantedUUID })
                    .then((result) => {
                        if (result?.success) {
                            this.createForm = {
                                uuid: this.data.uuid,
                                property: {
                                    company: this.data.company,
                                    registeredOwner: this.data.registeredOwner,
                                    description: this.data.description,
                                    remark: this.data.remark,
                                    address1: this.data.address1,
                                    address2: this.data.address2,
                                    address3: this.data.address3,
                                    city: this.data.city,
                                    state: this.data.state,
                                    postcode: this.data.postcode,
                                    lat: this.data.lat,
                                    lng: this.data.lng,
                                    isAsset: 0,
                                    isTenanted: 1,
                                    isMalaysia: this.data.isMalaysia,
                                    isPersonal: this.data.isPersonal,
                                    image: this.data.imageUrl,
                                    groupUuid: this.data.group ? {
                                        value: this.data.group.uuid,
                                        text: this.data.group.name,
                                    } : null,
                                    propertyTypeUuid: this.data.propertyType ? {
                                        value: this.data.propertyType.uuid,
                                        text: this.data.propertyType.name,
                                    } : null,
                                    locationUuid: this.data.location ? {
                                        value: this.data.location.uuid,
                                        text: this.data.location.name,
                                    } : null,
                                },
                                tenanted: {
                                    ...this.data.tenanted,
                                },
                            };
                        }
                    });
            }
        },
        uploadImage(image) {
            this.createForm.property.image = image[0]?.file;
        },
        removeImage() {
            this.createForm.property.image = null;
        },
        requestCreate() {
            this.submitted = true;
            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            } else {
                let data = {
                    property: {
                        ...this.createForm.property,
                        groupUuid: this.createForm.property.groupUuid?.value,
                        propertyTypeUuid: this.createForm.property.propertyTypeUuid?.value,
                        locationUuid: this.createForm.property.locationUuid?.value,
                        isMalaysia: this.createForm.property.isMalaysia ? 1 : 0,
                        isPersonal: this.createForm.property.isPersonal ? 1 : 0,
                    },
                    tenanted: {
                        ...this.createForm.tenanted,
                    },
                };
                this.createPropertyTenanted({data, filter: this.searchCriteria})
                    .then((result) => this.$nextTick(() => {
                        if (result?.success) {
                            this.resetForm();
                            this.$emit("done");
                        }
                    }));
            }
        },
        requestUpdate() {
            this.submitted = true;
            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            } else {
                let data = {
                    uuid: this.createForm.uuid,
                    property: {
                        ...this.createForm.property,
                        groupUuid: this.createForm.property.groupUuid?.value,
                        propertyTypeUuid: this.createForm.property.propertyTypeUuid?.value,
                        locationUuid: this.createForm.property.locationUuid?.value,
                        isMalaysia: this.createForm.property.isMalaysia ? 1 : 0,
                        isPersonal: this.createForm.property.isPersonal ? 1 : 0,
                    },
                    tenanted: {
                        ...this.createForm.tenanted,
                    },
                };

                this.updatePropertyTenanted({data, filter: this.searchCriteria})
                    .then((result) => this.$nextTick(() => {
                        if (result?.success) {
                            this.resetForm();
                            this.$emit("done");
                        }
                    }));
            }
        },
        resetForm() {
            this.createForm = this.defaultForm();
            this.submitted = false;
            this.$v.$reset();
        },
    },
    computed: {
        ...mapState(PROPERTY_TENANTED_MODULE, ['status']),
        data() {
            return this.$store.state.api.propertyTenanted.details;
        },
        groupDdl() {
            return this.$store.state.api.lookup.groupDdl || [];
        },
        propertyTypeDdl() {
            return this.$store.state.api.lookup.propertyTypeDdl || [];
        },
        locationDdl() {
            return this.$store.state.api.lookup.locationDdl || [];
        },
    },
}
</script>
